/**
 * Operator-lens classification and deep-link ownership for policy values
 * (DNKADM-0192 §3.1).
 *
 * This is render-neutral data. The Policies reference is read-only: configurable
 * values link to the screen that owns them; platform rules have no edit route.
 */
export type PolicyValueKey =
  | 'booking.windowMonths'
  | 'booking.serviceIntervalMinutes'
  | 'booking.guestHoldTtlMinutes'
  | 'currency'
  | 'membership.graceDays'
  | 'membership.maxReschedules'
  | 'cancellation.refundRoundingIncrement'
  | 'cancellation.refundRoundingMode'
  | 'cancellation.bands'
  | 'cancellation.defaultFullRefundMinHours'
  | 'reservation.reconciliationGraceMinutes'
  | 'reservation.deferredChargeMinutes'
  | 'variation.noShowFee'
  | 'service.taxable'
  | 'service.maxGuests'
  | 'location.taxRates'
  | 'location.hours';

type PolicyValueClassification = 'configurable' | 'platform-rule';
type PolicyValueChipLabel = 'Configurable' | 'Platform rule';
type PolicyTargetKind = 'exact' | 'closest-owner' | null;

export interface PolicyValueMapping {
  key: PolicyValueKey;
  classification: PolicyValueClassification;
  chipLabel: PolicyValueChipLabel;
  route: string | null;
  targetKind: PolicyTargetKind;
  note: string | null;
}

function configurable(
  key: PolicyValueKey,
  route: string,
  targetKind: Exclude<PolicyTargetKind, null> = 'exact',
  note: string | null = null,
): PolicyValueMapping {
  return {
    key,
    classification: 'configurable',
    chipLabel: 'Configurable',
    route,
    targetKind,
    note,
  };
}

function platformRule(key: PolicyValueKey): PolicyValueMapping {
  return {
    key,
    classification: 'platform-rule',
    chipLabel: 'Platform rule',
    route: null,
    targetKind: null,
    note: null,
  };
}

export const POLICY_VALUE_MAPPINGS: readonly PolicyValueMapping[] = [
  configurable('booking.windowMonths', '/tenant-settings'),
  configurable('booking.serviceIntervalMinutes', '/tenant-settings'),
  configurable('booking.guestHoldTtlMinutes', '/tenant-settings'),
  configurable(
    'currency',
    '/tenant-settings',
    'exact',
    'Currency locks after the facility records its first money or booking activity.',
  ),
  configurable('membership.graceDays', '/membership-credits/settings'),
  configurable('membership.maxReschedules', '/membership-credits/settings'),
  configurable('cancellation.refundRoundingIncrement', '/membership-credits/settings'),
  configurable('cancellation.refundRoundingMode', '/membership-credits/settings'),
  configurable('cancellation.bands', '/cancellation-policy'),
  platformRule('cancellation.defaultFullRefundMinHours'),
  platformRule('reservation.reconciliationGraceMinutes'),
  platformRule('reservation.deferredChargeMinutes'),
  configurable(
    'variation.noShowFee',
    '/services',
    'closest-owner',
    'Choose a service, then open a variation to edit its no-show fee.',
  ),
  configurable(
    'service.taxable',
    '/services',
    'closest-owner',
    'Choose a service to edit whether it is taxable.',
  ),
  configurable(
    'service.maxGuests',
    '/services',
    'closest-owner',
    'Choose a service to edit its guest limit.',
  ),
  configurable(
    'location.taxRates',
    '/locations',
    'closest-owner',
    'Choose a location, then open Tax information.',
  ),
  configurable(
    'location.hours',
    '/locations',
    'closest-owner',
    'Choose a location, then open Hours of operation.',
  ),
];

const POLICY_VALUE_MAPPING_BY_KEY = Object.fromEntries(
  POLICY_VALUE_MAPPINGS.map((mapping) => [mapping.key, mapping]),
) as Record<PolicyValueKey, PolicyValueMapping>;

export function policyValueMapping(key: PolicyValueKey): PolicyValueMapping {
  return POLICY_VALUE_MAPPING_BY_KEY[key];
}
